// Manual Booking (SMB-3541) — ops manual-entry screen.
// Records a flight/hotel reservation already booked + paid outside Zenvoya. No
// search, no availability, no payment: fill the form, submit, and the booking is
// persisted as confirmed/paid. See src/components/manual-booking/types.ts for the
// contract and manualBookingService.js for the API wiring.
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import FlightIcon from '@mui/icons-material/Flight';
import ApartmentIcon from '@mui/icons-material/Apartment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  createManualFlightBooking,
  createManualHotelBooking,
  getAdminId,
} from '../operations/manualBookingService';
import {
  FlightForm,
  HotelBookingForm,
  FieldErrors,
  BookingMeta,
  ManualBookingResult,
  emptyFlightForm,
  emptyHotelForm,
  validateFlightForm,
  validateHotelForm,
  buildFlightPayload,
  buildHotelPayload,
  isSuccessStatus,
  extractApiError,
  PAYMENT_TYPES,
  PaymentForm,
  emptyPayment,
} from '../components/manual-booking/types';
import ManualFlightForm from '../components/manual-booking/ManualFlightForm';
import ManualHotelForm from '../components/manual-booking/ManualHotelForm';
import { Field, SelectField, SectionTitle } from '../components/manual-booking/fields';
// @ts-ignore -- usersService is JS; named exports are valid at runtime
import { getUserByEmail, extractUserId, extractUserName } from '../operations/usersService';
// @ts-ignore -- helpers is JS
import { validateEmail } from '../helpers';

type BookingType = 'flight' | 'hotel';

const ManualBooking: React.FC = () => {
  // Booking type is driven by the sidebar sub-item route (/manual-booking/:type).
  const { type } = useParams<{ type: string }>();
  const bookingType: BookingType = type === 'hotel' ? 'hotel' : 'flight';

  const [meta, setMeta] = useState<BookingMeta>({
    userId: '',
    createdBy: getAdminId(),
    sendMail: true,
    payment: emptyPayment(),
  });

  // Customer lookup: ops enters an email, we resolve the userId via the service.
  const [email, setEmail] = useState('');
  const [lookup, setLookup] = useState<{ loading: boolean; error: string; name: string }>({
    loading: false,
    error: '',
    name: '',
  });

  const [flightForm, setFlightForm] = useState<FlightForm>(emptyFlightForm());
  const [hotelForm, setHotelForm] = useState<HotelBookingForm>(emptyHotelForm());

  const [errors, setErrors] = useState<FieldErrors>({});
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ManualBookingResult | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({
    open: false,
    msg: '',
    sev: 'success',
  });

  const setMetaField = (k: keyof BookingMeta, v: string) => setMeta((m) => ({ ...m, [k]: v }));
  const setPaymentField = (k: keyof PaymentForm, v: string) =>
    setMeta((m) => ({ ...m, payment: { ...m.payment, [k]: v } }));

  // Editing the email invalidates any previously resolved userId.
  const onEmailChange = (v: string) => {
    setEmail(v);
    setMeta((m) => ({ ...m, userId: '' }));
    setLookup({ loading: false, error: '', name: '' });
  };

  const handleFindUser = async () => {
    if (!validateEmail(email.trim())) {
      setLookup({ loading: false, error: 'Enter a valid email', name: '' });
      return;
    }
    setLookup({ loading: true, error: '', name: '' });
    try {
      const parsed: any = await getUserByEmail(email.trim());
      const uid = extractUserId(parsed);
      if (uid) {
        setMeta((m) => ({ ...m, userId: String(uid) }));
        setLookup({ loading: false, error: '', name: extractUserName(parsed) || '' });
        setErrors((e) => {
          const { userId, ...rest } = e;
          return rest;
        });
      } else {
        setMeta((m) => ({ ...m, userId: '' }));
        setLookup({
          loading: false,
          error: parsed?.message || 'No customer found for that email',
          name: '',
        });
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || 'Lookup failed — please try again';
      setMeta((m) => ({ ...m, userId: '' }));
      setLookup({ loading: false, error: msg, name: '' });
    }
  };

  // Switching Flight <-> Hotel from the sidebar clears transient result/errors
  // (the entered form data for each type is preserved in its own state).
  React.useEffect(() => {
    setErrors({});
    setServerErrors([]);
    setResult(null);
  }, [bookingType]);

  const resetForm = () => {
    if (bookingType === 'flight') setFlightForm(emptyFlightForm());
    else setHotelForm(emptyHotelForm());
    setErrors({});
    setServerErrors([]);
    setResult(null);
  };

  const handleSubmit = async () => {
    const fieldErrors =
      bookingType === 'flight'
        ? validateFlightForm(flightForm, meta)
        : validateHotelForm(hotelForm, meta);
    setErrors(fieldErrors);
    setServerErrors([]);
    setResult(null);
    if (Object.keys(fieldErrors).length > 0) {
      setSnack({ open: true, msg: 'Please fix the highlighted fields', sev: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      const res =
        bookingType === 'flight'
          ? await createManualFlightBooking(buildFlightPayload(flightForm, meta))
          : await createManualHotelBooking(buildHotelPayload(hotelForm, meta));

      if (isSuccessStatus(res?.status)) {
        setResult((res?.data as ManualBookingResult) || {});
        setSnack({
          open: true,
          msg: res?.message || 'Manual booking saved successfully.',
          sev: 'success',
        });
        return;
      }
      // Non-2xx envelope returned in-band (gateway normalizes some errors to 200).
      const { message, errors: errs } = extractApiError(res);
      setServerErrors(errs.length ? errs : [message]);
      setSnack({ open: true, msg: message, sev: 'error' });
    } catch (err) {
      const { message, errors: errs } = extractApiError(err);
      setServerErrors(errs.length ? errs : [message]);
      setSnack({ open: true, msg: message, sev: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className="manualBookingPage">
      <Box className="headingBtnWrap">
        <Typography variant="h1">Manual Booking</Typography>
        <Box
          display="flex"
          alignItems="center"
          gap={0.75}
          sx={{
            color: '#272727',
            fontWeight: 700,
            px: 1.5,
            py: 0.5,
            border: '1px solid #e1e8ed',
            borderRadius: '20px',
            backgroundColor: '#fff',
          }}
        >
          {bookingType === 'flight' ? (
            <FlightIcon fontSize="small" />
          ) : (
            <ApartmentIcon fontSize="small" />
          )}
          <Typography component="span" sx={{ fontWeight: 700 }}>
            {bookingType === 'flight' ? 'Flight' : 'Hotel'}
          </Typography>
        </Box>
      </Box>

      <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
        Record a {bookingType} reservation that was already booked and paid for outside Zenvoya.
        No availability or payment is processed — it is saved as a confirmed, paid booking.
      </Typography>

      {result ? (
        // ------------------------------------------------ Result panel
        <Paper variant="outlined" sx={{ p: 3, maxWidth: 640 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <CheckCircleIcon sx={{ color: '#2e7d32' }} />
            <Typography variant="h6">Booking saved</Typography>
          </Box>
          <Box component="dl" sx={{ m: 0 }}>
            {[
              ['Itinerary ID', result.itineraryID],
              ['Transaction ID', result.transactionId],
              ['TID', result.tid],
              ['Booking Type', result.bookingType],
            ]
              .filter(([, v]) => v)
              .map(([k, v]) => (
                <Box key={k as string} display="flex" gap={2} mb={1}>
                  <Typography sx={{ minWidth: 140, color: '#666' }}>{k}</Typography>
                  <Typography sx={{ fontWeight: 600, wordBreak: 'break-all' }}>{v}</Typography>
                </Box>
              ))}
          </Box>
          <Box component="dl" sx={{ m: 0, mt: 1 }}>
            <Box display="flex" gap={2} mb={1}>
              <Typography sx={{ minWidth: 140, color: '#666' }}>Confirmation email</Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {result.emailSent ? 'Sent' : 'Not sent'}
              </Typography>
            </Box>
            <Box display="flex" gap={2} mb={1}>
              <Typography sx={{ minWidth: 140, color: '#666' }}>MyTrips sync (SQS)</Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {result.sqsEventPublished ? 'Published' : 'Pending'}
              </Typography>
            </Box>
          </Box>
          {result.sqsEventPublished === false && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Saved. The MyTrips refresh notification wasn't published — it may take a moment to
              appear in MyTrips.
            </Alert>
          )}
          <Box display="flex" gap={2} mt={3}>
            <Button
              variant="contained"
              onClick={resetForm}
              sx={{ backgroundColor: '#272727', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#111' } }}
            >
              New booking
            </Button>
          </Box>
        </Paper>
      ) : (
        <Box>
          {serverErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {serverErrors.length === 1 ? (
                serverErrors[0]
              ) : (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {serverErrors.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              )}
            </Alert>
          )}

          <Box className="feeModalForm">
            {/* Shared booking meta — resolve the customer by email */}
            <Box className="feeInputFieldWrap" sx={{ alignItems: 'flex-start' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <Field
                  label="Customer Email"
                  required
                  type="email"
                  value={email}
                  onChange={onEmailChange}
                  error={lookup.error || errors.userId}
                  placeholder="customer@example.com"
                />
                <Button
                  variant="contained"
                  onClick={handleFindUser}
                  disabled={lookup.loading || !email.trim()}
                  startIcon={lookup.loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                  sx={{
                    height: 38,
                    mb: '2px',
                    backgroundColor: '#272727',
                    color: '#fff',
                    fontWeight: 'bold',
                    '&:hover': { backgroundColor: '#111' },
                  }}
                >
                  Find
                </Button>
              </Box>
              <Field
                label="Customer User ID"
                required
                readOnly
                value={meta.userId}
                onChange={() => {}}
                placeholder="resolved from email"
              />
              <Field
                label="Created By (ops agent)"
                value={meta.createdBy}
                onChange={(v) => setMetaField('createdBy', v)}
                placeholder="admin"
              />
              <div className="form-control inputFieldWrap">
                <label>Send Mail To Ops Agent?</label>
                <RadioGroup
                  row
                  value={meta.sendMail ? 'yes' : 'no'}
                  onChange={(e) => setMeta((m) => ({ ...m, sendMail: e.target.value === 'yes' }))}
                  sx={{ height: 38, alignItems: 'center', flexWrap: 'nowrap' }}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio size="small" sx={{ py: 0, color: '#272727', '&.Mui-checked': { color: '#272727' } }} />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio size="small" sx={{ py: 0, color: '#272727', '&.Mui-checked': { color: '#272727' } }} />}
                    label="No"
                  />
                </RadioGroup>
              </div>
            </Box>
            {meta.userId && (
              <Box display="flex" alignItems="center" gap={0.75} sx={{ mt: -0.5, mb: 1, color: '#2e7d32' }}>
                <CheckCircleIcon fontSize="small" />
                <Typography variant="body2">
                  Customer resolved{lookup.name ? `: ${lookup.name}` : ''} ({meta.userId})
                </Typography>
              </Box>
            )}

            {/* Payment — how the customer paid ops (written to the Payments tab). */}
            <SectionTitle>Payment</SectionTitle>
            <Box className="feeInputFieldWrap">
              <SelectField
                label="Payment Type"
                required
                value={meta.payment.paymentType}
                onChange={(v) => setPaymentField('paymentType', v)}
                options={PAYMENT_TYPES.map((p) => ({ value: p.value, label: p.label }))}
                error={errors['payment.paymentType']}
              />
              <Field
                label="Customer Name"
                value={meta.payment.customerName}
                onChange={(v) => setPaymentField('customerName', v)}
                placeholder="defaults to lead traveller"
              />
              {meta.payment.paymentType === 'CARD' && (
                <>
                  <Field
                    label="Card Type"
                    value={meta.payment.cardType}
                    onChange={(v) => setPaymentField('cardType', v)}
                    placeholder="VISA / MASTERCARD"
                  />
                  <Field
                    label="Card Number (last 4)"
                    value={meta.payment.cardNumber}
                    onChange={(v) => setPaymentField('cardNumber', v)}
                    placeholder="**** **** **** 1234"
                  />
                  <Field
                    label="Card Expiry"
                    type="date"
                    value={meta.payment.cardExpiry}
                    onChange={(v) => setPaymentField('cardExpiry', v)}
                    error={errors['payment.cardExpiry']}
                  />
                </>
              )}
              <Field
                label="Remarks"
                value={meta.payment.remarks}
                onChange={(v) => setPaymentField('remarks', v)}
                placeholder="e.g. Paid at counter"
              />
            </Box>

            {bookingType === 'flight' ? (
              <ManualFlightForm form={flightForm} onChange={setFlightForm} errors={errors} />
            ) : (
              <ManualHotelForm form={hotelForm} onChange={setHotelForm} errors={errors} />
            )}

            <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <Button variant="outlined" onClick={resetForm} disabled={submitting} sx={{ minWidth: 120, height: 40 }}>
                Reset
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
                sx={{
                  minWidth: 160,
                  height: 40,
                  backgroundColor: '#272727',
                  color: '#fff',
                  fontWeight: 'bold',
                  '&:hover': { backgroundColor: '#111' },
                }}
              >
                Save Booking
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManualBooking;
